import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const checkAuth = useCallback(async () => {
        // Check if we have a token stored
        const token = localStorage.getItem('session_token');
        if (!token) {
            setLoading(false);
            return;
        }
        
        try {
            const response = await authAPI.getMe();
            setUser(response.data);
            setError(null);
        } catch (err) {
            setUser(null);
            // Clear invalid token
            if (err.response?.status === 401) {
                localStorage.removeItem('session_token');
            }
            if (err.response?.status !== 401) {
                setError('Failed to authenticate');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
        
        // Listen for session invalidation events from API interceptor
        const handleSessionInvalid = () => {
            console.log('Session invalidated, clearing user state');
            setUser(null);
            localStorage.removeItem('session_token');
        };
        
        window.addEventListener('auth-session-invalid', handleSessionInvalid);
        
        return () => {
            window.removeEventListener('auth-session-invalid', handleSessionInvalid);
        };
    }, [checkAuth]);

    // Email/Password Registration
    const register = async (email, password, name) => {
        try {
            setLoading(true);
            setError(null);
            const response = await authAPI.register({ email, password, name });
            // Store token if provided
            if (response.data?.session_token) {
                localStorage.setItem('session_token', response.data.session_token);
            }
            setUser(response.data);
            return response.data;
        } catch (err) {
            const message = err.response?.data?.detail || 'Registration failed';
            setError(message);
            throw new Error(message);
        } finally {
            setLoading(false);
        }
    };

    // Email/Password Login
    const loginWithEmail = async (email, password) => {
        try {
            setLoading(true);
            setError(null);
            const response = await authAPI.login({ email, password });
            // Store token if provided
            if (response.data?.session_token) {
                localStorage.setItem('session_token', response.data.session_token);
            }
            setUser(response.data);
            return response.data;
        } catch (err) {
            const message = err.response?.data?.detail || 'Login failed';
            setError(message);
            throw new Error(message);
        } finally {
            setLoading(false);
        }
    };

    // Google OAuth Login - redirects to Google
    const loginWithGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/auth/callback';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    // Process Google OAuth session (called from AuthCallback)
    const processSession = async (sessionId) => {
        try {
            setLoading(true);
            const response = await authAPI.processSession(sessionId);
            // Store token if provided
            if (response.data?.session_token) {
                localStorage.setItem('session_token', response.data.session_token);
            }
            setUser(response.data);
            setError(null);
            return response.data;
        } catch (err) {
            setError('Failed to process session');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            await authAPI.logout();
        } catch (err) {
            console.error('Logout error:', err);
        } finally {
            // Always clear local state
            localStorage.removeItem('session_token');
            setUser(null);
        }
    };

    const refreshUser = async () => {
        await checkAuth();
    };

    const clearError = () => setError(null);

    const value = {
        user,
        loading,
        error,
        register,
        loginWithEmail,
        loginWithGoogle,
        login: loginWithGoogle, // Alias for backwards compatibility
        processSession,
        logout,
        refreshUser,
        clearError,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
        isSuperAdmin: user?.role === 'super_admin',
        isStaff: user?.role === 'staff',
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
